import React, { useState } from "react";
import { X, Phone, Lock, CheckCircle2, ShieldCheck } from "lucide-react";
import { api } from "../services/api";

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const AuthModal: React.FC<AuthModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const [authTab, setAuthTab] = useState<"login" | "register" | "forgot">("login");
  
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-end md:items-center justify-center bg-black/60 backdrop-blur-sm p-0 md:p-4">
      {/* Modal Container */}
      <div className="bg-white w-full max-w-[450px] rounded-t-[32px] md:rounded-[32px] shadow-2xl overflow-hidden animate-in slide-in-from-bottom duration-300">
        
        {/* Mobile Handle */}
        <div className="md:hidden w-12 h-1.5 bg-gray-200 rounded-full mx-auto mt-4 mb-2" />
        
        <div className="p-8">
          <div className="flex justify-between items-start mb-6">
            <div>
              <h2 className="text-2xl font-black text-gray-900 leading-tight">
                {authTab === "login" ? "Welcome Back!" : authTab === "register" ? "Create Account" : "Reset Password"}
              </h2>
              <p className="text-gray-500 text-sm font-medium mt-1 flex items-center gap-1">
                <ShieldCheck size={14} className="text-green-500" /> 
                Secure verification to see best price
              </p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-400">
              <X size={20} />
            </button>
          </div>

          {/* Tab Row */}
          {authTab !== "forgot" && (
            <div className="flex bg-gray-100 p-1.5 rounded-2xl mb-8">
              <button
                onClick={() => setAuthTab("login")}
                className={`flex-1 py-3 text-sm font-bold rounded-xl transition-all ${
                  authTab === "login" ? "bg-white text-[#363C90] shadow-sm" : "text-gray-500 hover:text-gray-700"
                }`}
              >
                Login
              </button>
              <button
                onClick={() => setAuthTab("register")}
                className={`flex-1 py-3 text-sm font-bold rounded-xl transition-all ${
                  authTab === "register" ? "bg-white text-[#363C90] shadow-sm" : "text-gray-500 hover:text-gray-700"
                }`}
              >
                Register
              </button>
            </div>
          )}

          {/* Forms Section */}
          <div className="min-h-[320px]">
            {authTab === "login" && (
              <LoginForm onSuccess={onSuccess} onForgot={() => setAuthTab("forgot")} onClose={onClose} />
            )}
            {authTab === "register" && (
              <RegisterForm onSuccess={onSuccess} onClose={onClose} />
            )}
            {authTab === "forgot" && (
              <ForgotPasswordForm onBack={() => setAuthTab("login")} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/* --- Sub-Components --- */

const LoginForm = ({ onSuccess, onForgot, onClose }: any) => {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ mobile: "", password: "" });

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post("/auth/login", form);
      localStorage.setItem("user_token", res.token);
      onSuccess();
      onClose(); // Modal will close here automatically
    } catch (err: any) {
      alert(err.message || "Login failed. Please check credentials.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleLogin} className="space-y-4">
      <div className="relative">
        <Phone className="absolute left-4 top-4 text-gray-400" size={20} />
        <input
          required
          type="tel"
          placeholder="Mobile Number"
          className="w-full pl-12 pr-4 py-4 bg-gray-50 border-2 border-transparent focus:border-[#363C90] focus:bg-white rounded-2xl outline-none transition-all font-medium"
          onChange={(e) => setForm({ ...form, mobile: e.target.value })}
        />
      </div>
      <div className="relative">
        <Lock className="absolute left-4 top-4 text-gray-400" size={20} />
        <input
          required
          type="password"
          placeholder="Password"
          className="w-full pl-12 pr-4 py-4 bg-gray-50 border-2 border-transparent focus:border-[#363C90] focus:bg-white rounded-2xl outline-none transition-all font-medium"
          onChange={(e) => setForm({ ...form, password: e.target.value })}
        />
      </div>
      <button type="button" onClick={onForgot} className="text-[#363C90] text-sm font-bold float-right hover:underline">
        Forgot Password?
      </button>
      <button
        type="submit"
        disabled={loading}
        className="w-full py-4 bg-[#363C90] hover:bg-[#2a2f70] text-white rounded-2xl font-black text-lg shadow-xl shadow-indigo-900/20 transition-all active:scale-95 disabled:opacity-50 mt-4"
      >
        {loading ? "AUTHENTICATING..." : "LOGIN NOW"}
      </button>
    </form>
  );
};

const RegisterForm = ({ onSuccess, onClose }: any) => {
  const [loading, setLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [form, setForm] = useState({ mobile: "", password: "", otp: "" });

  const handleAction = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (!otpSent) {
        await api.post("/auth/send-otp", { mobile: form.mobile });
        setOtpSent(true);
      } else {
        const res = await api.post("/auth/register", form);
        localStorage.setItem("user_token", res.token);
        onSuccess();
        onClose(); // Modal will close here automatically
      }
    } catch (err: any) {
      alert(err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleAction} className="space-y-4">
      <div className="relative">
        <Phone className="absolute left-4 top-4 text-gray-400" size={20} />
        <input
          required
          type="tel"
          disabled={otpSent}
          placeholder="Mobile Number"
          className="w-full pl-12 pr-4 py-4 bg-gray-50 border-2 border-transparent focus:border-[#363C90] rounded-2xl outline-none font-medium disabled:opacity-60"
          onChange={(e) => setForm({ ...form, mobile: e.target.value })}
        />
      </div>
      <div className="relative">
        <Lock className="absolute left-4 top-4 text-gray-400" size={20} />
        <input
          required
          type="password"
          placeholder="Create Password"
          className="w-full pl-12 pr-4 py-4 bg-gray-50 border-2 border-transparent focus:border-[#363C90] rounded-2xl outline-none font-medium"
          onChange={(e) => setForm({ ...form, password: e.target.value })}
        />
      </div>
      {otpSent && (
        <div className="animate-in slide-in-from-top-2 duration-300">
          <input
            required
            type="text"
            placeholder="Enter OTP"
            maxLength={6}
            className="w-full px-6 py-4 bg-white border-2 border-[#363C90] rounded-2xl outline-none font-bold text-center tracking-[10px] text-xl"
            onChange={(e) => setForm({ ...form, otp: e.target.value })}
          />
        </div>
      )}
      <button
        type="submit"
        disabled={loading}
        className="w-full py-4 bg-[#363C90] text-white rounded-2xl font-black text-lg shadow-xl shadow-indigo-900/20 mt-2 transition-all active:scale-95"
      >
        {loading ? "PROCESSING..." : otpSent ? "VERIFY & REGISTER" : "SEND OTP"}
      </button>
    </form>
  );
};

const ForgotPasswordForm = ({ onBack }: any) => {
  const [loading, setLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [form, setForm] = useState({ mobile: "", otp: "", password: "" });

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (!otpSent) {
        await api.post("/auth/forgot-otp", { mobile: form.mobile });
        setOtpSent(true);
      } else {
        await api.post("/auth/reset-password", form);
        alert("Password reset successfully! Please login.");
        onBack();
      }
    } catch (err: any) {
      alert(err.message || "Reset failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleReset} className="space-y-4">
      <input
        required
        type="tel"
        placeholder="Registered Mobile Number"
        className="w-full px-6 py-4 bg-gray-50 border-2 border-transparent focus:border-[#363C90] rounded-2xl outline-none"
        onChange={(e) => setForm({ ...form, mobile: e.target.value })}
      />
      {otpSent && (
        <>
          <input
            required
            type="text"
            placeholder="OTP"
            className="w-full px-6 py-4 bg-white border-2 border-[#363C90] rounded-2xl outline-none text-center tracking-widest font-bold"
            onChange={(e) => setForm({ ...form, otp: e.target.value })}
          />
          <input
            required
            type="password"
            placeholder="New Password"
            className="w-full px-6 py-4 bg-gray-50 border-2 border-transparent focus:border-[#363C90] rounded-2xl outline-none"
            onChange={(e) => setForm({ ...form, password: e.target.value })}
          />
        </>
      )}
      <button type="submit" disabled={loading} className="w-full py-4 bg-[#363C90] text-white rounded-2xl font-black">
        {otpSent ? "UPDATE PASSWORD" : "GET OTP"}
      </button>
      <button type="button" onClick={onBack} className="w-full text-gray-400 font-bold text-sm">
        Back to Login
      </button>
    </form>
  );
};

export default AuthModal;